import { useQueries, useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  CalculatorResponse,
  ThemeSchemaResponse,
  TimeSeriesData,
  themeOverview,
} from '@toggle/toggle';
import { formatInTimeZone } from 'date-fns-tz';

import { fetchTheme } from '~/services/overview-widget/overview-widget-service';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import { wretchRequest } from '~/utils/api-fetch/apiFetch';

const QUERY_KEY_THEME_PERFORMANCE = 'QUERY_KEY_THEME_PERFORMANCE';
const QUERY_KEY_THEME_PERFORMANCE_SPX = 'QUERY_KEY_THEME_PERFORMANCE_SPX';
const QUERY_KEY_THEME = 'QUERY_KEY_THEME';

type UseThemePerformanceData = {
  aggregate: { index: string; value: number }[];
  entities: Record<string, { index: string; value: number }[]>;
  allEntityTags: string[];
};
const SPX_TAG = 'spx';

export const useThemePerformance = (entities?: string[], numDays?: number) => {
  const { path, schema } = themeOverview.themeCalculator;

  return useQuery({
    queryKey: [QUERY_KEY_THEME_PERFORMANCE, entities, numDays],
    queryFn: () =>
      wretchRequest<CalculatorResponse>(path, {
        schema,
        method: 'post',
        body: {
          entities: entities,
          n_days: numDays,
        },
      }),
    enabled: !!entities && !!entities.length,
    staleTime: getTimeTillEndOfDayMs(),
    select: (data): UseThemePerformanceData => {
      const filteredEntities = Object.fromEntries(
        Object.entries(data.entities ?? {}).filter(([_, perf]) => perf !== null)
      ) as Record<string, { index: string; value: number }[]>;

      return {
        aggregate: data.aggregate,
        entities: filteredEntities,
        allEntityTags: Object.keys(data.entities ?? {}),
      };
    },
  });
};

export const useSpxBenchmark = (fromDate?: string) => {
  const { path, schema } = themeOverview.spxBenchmark;
  const formattedFromDate = fromDate
    ? formatInTimeZone(fromDate, 'UTC', 'yyyyMMdd')
    : '';

  return useQuery({
    queryKey: [QUERY_KEY_THEME_PERFORMANCE_SPX, fromDate],
    queryFn: () =>
      wretchRequest<TimeSeriesData>(path, {
        schema: schema,
        method: 'post',
        body: {
          snake_expression: `${SPX_TAG}.price.cumulative_returns(date=${formattedFromDate})`,
          from_date: fromDate,
        },
      }),
    enabled: !!fromDate,
    staleTime: getTimeTillEndOfDayMs(),
    select: (data): UseThemePerformanceData => {
      return {
        aggregate: data.result.data.map(i => ({ ...i, value: i.value * 100 })),
        entities: {
          [SPX_TAG]: data.result.data
            .filter(perf => perf !== null)
            .map(data => ({ ...data, value: data.value * 100 })),
        },
        allEntityTags: [SPX_TAG],
      };
    },
  });
};

export const useTheme = ({ themeId = '' }: { themeId?: string }) => {
  const language = useLanguageStore(state => state.languageSetting);
  return useQuery<ThemeSchemaResponse>({
    queryKey: [QUERY_KEY_THEME, themeId, language],
    queryFn: ({ signal }) => fetchTheme({ themeId, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!themeId,
  });
};

export const useThemesArray = (themeIds: string[]) => {
  const language = useLanguageStore(state => state.languageSetting);
  const queries = themeIds.map(themeId => ({
    queryKey: [QUERY_KEY_THEME, themeId, language],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchTheme({ themeId, signal }),
    staleTime: getTimeTillEndOfDayMs(),
  }));

  return useQueries({
    queries,
    combine: results => {
      const hasResult = !!results.length;
      return {
        data: results
          .map(result => result.data)
          .filter(Boolean) as ThemeSchemaResponse[],
        isFetching: hasResult && results.some(result => result.isFetching),
        isError: hasResult && results.every(result => result.isError),
      };
    },
  });
};
